import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { keyBy } from 'lodash';
import { useCallback } from 'react';
import ICursorable from 'types/Cursorable';
import IIdable from 'types/Idable';
import { ReactQueryInfiniteQueryData } from 'types/react-query/ReactQueryInfiniteQueryData';
import { mapNotNull } from 'utils/collectionUtils';

export type UseReactQueryLazyLoadingUpdateData<K extends string | number = string> = ICursorable & IIdable<K>;
// TODO: Add could be added but need to think through position arguments
export type UseReactQueryLazyLoadingAction = 'update';
export type UseReactQueryLazyLoadingUpdatePayload<K extends string | number = string> =
  UseReactQueryLazyLoadingUpdaterPayload<K>;

type UseReactQueryLazyLoadingUpdaterPayload<K extends string | number = string> = IIdable<K> & {
  item: UseReactQueryLazyLoadingUpdateData<K>;
  action: {
    type: 'update';
  };
};

export default function useReactQueryLazyLoadingUpdate<
  UpdateData extends UseReactQueryLazyLoadingUpdatePayload<K>,
  K extends string | number = string,
>(queryKey: QueryKey) {
  const queryClient = useQueryClient();

  return useCallback(
    (data: Array<Omit<UpdateData, 'cursor'>>) => {
      if (data.length === 0) {
        return;
      }

      const keyedData = keyBy(data, (datum) => datum.id) as Record<K, UpdateData>;

      queryClient.setQueryData(
        queryKey,
        (currentData: ReactQueryInfiniteQueryData<UseReactQueryLazyLoadingUpdateData<K>>) => {
          if (!currentData || !currentData.pages || !Array.isArray(currentData.pages)) {
            return currentData;
          }

          const newPages = currentData.pages.map((page) => {
            if (!page || !Array.isArray(page)) {
              return page;
            }

            return mapNotNull(page, (item) => {
              const itemPayload = keyedData[item.id];

              if (!itemPayload) {
                return item;
              }

              if (itemPayload.action.type === 'update') {
                // Keep the original cursor to not break paging
                return { ...(itemPayload as UseReactQueryLazyLoadingUpdaterPayload<K>).item, cursor: item.cursor };
              }

              return item;
            });
          });

          return { ...currentData, pages: newPages };
        }
      );
    },
    [queryClient, queryKey]
  );
}

export function convertModelToReactQueryLazyLoadingUpdateUpdate<
  T extends UseReactQueryLazyLoadingUpdateData<K>,
  K extends string | number = string,
>(model: T): UseReactQueryLazyLoadingUpdaterPayload<K> {
  return {
    id: model.id,
    item: model,
    action: {
      type: 'update',
    },
  };
}
